import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PhoneIcon } from 'lucide-react';
import { business, routes } from '../data/site';
import { featuredProducts } from '../data/products';
import { shopCategories, shopCategoryGroups } from '../data/shopCategories';
import { SiteLink } from '../components/SiteLink';
import { ProductCard } from '../components/ProductCard';
const TITLE = 'Shop Spa Supplies, Chemicals & Big Green Egg Accessories | Pegasus Spas';
const DESCRIPTION = 'Shop hot tub filters, spa and pool chemicals, testing and care supplies, and Big Green Egg accessories from Pegasus Spas in Brooklyn, Michigan.';
const CANONICAL = 'https://www.pegasus-spas.com/3.html';
const sortOptions = [{
  value: 'featured',
  label: 'Featured'
}, {
  value: 'name',
  label: 'Name A–Z'
}, {
  value: 'price-asc',
  label: 'Price low to high'
}, {
  value: 'price-desc',
  label: 'Price high to low'
}];
const helpItems = [{
  title: 'Find the Right Filter',
  body: 'Bring the old filter or send clear photos of it. If a model or year would help confirm the match, we can show you where to find it.',
  href: routes.filters,
  linkLabel: 'Shop Filters'
}, {
  title: 'Water-Care Questions',
  body: 'Use our spa care guide, or tell us what your water is doing and we’ll help.',
  href: routes.spaCare,
  linkLabel: 'Spa Care & FAQs'
}, {
  title: 'Prefer to Shop in Person?',
  body: `Visit ${business.street}, ${business.cityStateZip}.`,
  href: routes.location,
  linkLabel: 'Location & Hours'
}];
const controlClass = 'mt-1.5 block w-full rounded-btn border border-ink/30 bg-white px-3 py-2.5 text-[16px] text-ink transition-colors duration-150 ease-out placeholder:text-ink/45 focus:border-pegasus-blue focus:outline-none focus:ring-2 focus:ring-pegasus-blue/30';
function upsertMeta(name: string, content: string) {
  let el = document.querySelector<HTMLMetaElement>(`meta[name="${name}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute('name', name);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}
export function ShopPage() {
  const [query, setQuery] = useState('');
  const [sort, setSort] = useState('featured');
  const [cartCount, setCartCount] = useState(0);
  const navigate = useNavigate();
  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = TITLE;
    upsertMeta('description', DESCRIPTION);
    let canonical = document.querySelector<HTMLLinkElement>('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', CANONICAL);
  }, []);
  const visibleProducts = useMemo(() => {
    const term = query.trim().toLowerCase();
    const filtered = term ? featuredProducts.filter(product => product.name.toLowerCase().includes(term) || product.sku.includes(term)) : featuredProducts;
    const sorted = [...filtered];
    if (sort === 'name') sorted.sort((a, b) => a.name.localeCompare(b.name));
    if (sort === 'price-asc') sorted.sort((a, b) => a.price - b.price);
    if (sort === 'price-desc') sorted.sort((a, b) => b.price - a.price);
    return sorted;
  }, [query, sort]);
  return <main data-id="element-1951" data-mp="24uwwii4bwyk8">
      {/* Breadcrumb + shop header */}
      <section aria-labelledby="shop-h1" className="w-full bg-water" data-id="element-1952" data-mp="1h2g71mi9i4aw">
        <div className="mx-auto max-w-container px-5 py-8 sm:px-8 lg:py-10" data-id="element-1953" data-mp="1c4elmuxd5sw9">
          <nav aria-label="Breadcrumb" data-id="element-1954" data-mp="1c695e5wwfeyr">
            <ol className="flex flex-wrap items-center gap-x-2 text-[15px] text-ink/70" data-id="element-1955" data-mp="1q467fhu4gdkt">
              <li data-id="element-1956" data-mp="1m1t2uwr42lpm">
                <SiteLink to={routes.home} className="underline decoration-ink/30 underline-offset-4 transition-colors duration-150 ease-out hover:text-pegasus-blue" data-id="element-1957" data-mp="1m19il9dznl8f">
                  Home
                </SiteLink>
              </li>
              <li aria-hidden="true" data-id="element-1958" data-mp="3ssfvcgpj2881">/</li>
              <li aria-current="page" className="font-medium text-ink" data-id="element-1959" data-mp="3sth5miypyqop">
                Shop Online
              </li>
            </ol>
          </nav>

          <div className="mt-5 grid grid-cols-1 gap-8 lg:grid-cols-12 lg:gap-12" data-id="element-1960" data-mp="5mf1u1nyk5xo">
            <div className="lg:col-span-7" data-id="element-1961" data-mp="alrreu67rr3y">
              <p className="text-[14px] font-bold uppercase text-pegasus-blue" data-id="element-1962" data-mp="amujh5f9vzlg">
                Pegasus Spas Online Store
              </p>
              <h1 id="shop-h1" className="mt-2 text-h2-mobile font-semibold text-ink lg:text-[40px] lg:leading-[48px]" data-id="element-1963" data-mp="fjbcoe1bx45e">
                Supplies, Chemicals &amp; Big Green Egg Accessories
              </h1>
              <p className="mt-4 text-body text-ink/80 sm:text-body-lg" data-id="element-1964" data-mp="tb7tvn1gltn0">
                Order the filters, water-care products and grilling accessories we carry in the
                showroom, and pick up the everyday things that keep your spa and your EGG ready to
                use.
              </p>
            </div>

            <div className="lg:col-span-5" data-id="element-1965" data-mp="2nqodhehgkn1l">
              <div className="border-t border-ink/20 pt-4" data-id="element-1966" data-mp="2it4zmfooaca6">
                <p className="text-[16px] leading-6 text-ink/85" data-id="element-1967" data-mp="2ivr426g9obo6">
                  Not sure which filter or care product fits? Call or text {business.phone}, bring
                  the old filter, or send a few clear photos. If model information would help, we
                  can show you where to find it.
                </p>
                <div className="mt-4 flex flex-wrap items-center gap-3" data-id="element-1968" data-mp="31jmebgco77cj">
                  <a href={business.phoneHref} className="inline-flex min-h-[48px] items-center gap-2 whitespace-nowrap rounded-btn bg-pegasus-blue px-5 text-[16px] font-bold text-white transition-colors duration-150 ease-out hover:bg-ink" data-id="element-1969" data-mp="31mielbbzq845">
                    <PhoneIcon className="h-4 w-4" aria-hidden="true" data-id="element-1970" data-mp="3pbl0ivdnqn4g" />
                    Call or Text
                  </a>
                  <SiteLink to={routes.spaCare} className="inline-flex min-h-[48px] items-center whitespace-nowrap rounded-btn border border-ink/25 bg-white px-5 text-[16px] font-bold text-ink transition-colors duration-150 ease-out hover:border-pegasus-blue" data-id="element-1971" data-mp="1ikyaapzfgsvj">
                    Spa Care &amp; FAQs
                  </SiteLink>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Catalog tools */}
      <section aria-labelledby="tools-heading" className="w-full border-y border-ink/10 bg-white" data-id="element-1972" data-mp="13xmwehfrwws9">
        <h2 id="tools-heading" className="sr-only" data-id="element-1973" data-mp="pxu9yyba52q3">
          Catalog tools
        </h2>
        <div className="mx-auto max-w-container px-5 py-5 sm:px-8" data-id="element-1974" data-mp="uv3ymbquhacv">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3" data-id="element-1975" data-mp="2katvt9696t5t">
            <div data-id="element-1976" data-mp="2kbmtzlr5cacc">
              <label htmlFor="product-search" className="block text-[15px] font-semibold text-ink" data-id="element-1977" data-mp="2k90orsrqa6xa">
                Search products
              </label>
              <input id="product-search" type="search" value={query} onChange={event => setQuery(event.target.value)} placeholder="Filter, charcoal, test strips…" className={controlClass} data-id="element-1978" data-mp="dnmfztar5jrz" />
            </div>

            <div data-id="element-1979" data-mp="ik33qme1hw4j">
              <label htmlFor="category-jump" className="block text-[15px] font-semibold text-ink" data-id="element-1980" data-mp="ijl4rhzzt3sx">
                Category
              </label>
              <select id="category-jump" defaultValue="" onChange={event => {
              if (event.target.value) navigate(event.target.value);
            }} className={controlClass} data-id="element-1981" data-mp="rera6fvcpjux">
                <option value="" data-id="element-1982" data-mp="382bwxyl98h33">Go to a category…</option>
                {shopCategories.map(category => <option key={category.href} value={category.href} data-id="element-1983" data-mp="3341zfhc254eb">
                    {category.label}
                  </option>)}
              </select>
            </div>

            <div data-id="element-1984" data-mp="2m96738jxrww9">
              <label htmlFor="sort-by" className="block text-[15px] font-semibold text-ink" data-id="element-1985" data-mp="2m8o8445w34kn">
                Sort by
              </label>
              <select id="sort-by" value={sort} onChange={event => setSort(event.target.value)} className={controlClass} data-id="element-1986" data-mp="2r0xz7niqqaoi">
                {sortOptions.map(option => <option key={option.value} value={option.value} data-id="element-1987" data-mp="239sccoedui4p">
                    {option.label}
                  </option>)}
              </select>
            </div>
          </div>

          <div className="mt-4 flex flex-wrap items-center justify-between gap-x-6 gap-y-1" data-id="element-1988" data-mp="2cbibyhif4ndk">
            <p className="text-[15.5px] text-ink/70" data-id="element-1989" data-mp="3jplgl80dcfws">Browse featured products below.</p>
            <p aria-live="polite" className="text-[15.5px] text-ink/70" data-id="element-1990" data-mp="3npvy9hxiwurh">
              {cartCount > 0 ? `Cart: ${cartCount} item${cartCount === 1 ? '' : 's'} (preview only)` : ''}
            </p>
          </div>
        </div>
      </section>

      {/* Shop by category */}
      <section aria-labelledby="category-heading" className="w-full bg-white" data-id="element-1991" data-mp="13a8b4p23916x">
        <div className="mx-auto max-w-container px-5 py-12 sm:px-8 lg:py-14" data-id="element-1992" data-mp="yboo0lbrdjcz">
          <h2 id="category-heading" className="text-h2-mobile font-semibold text-ink lg:text-h2-desktop" data-id="element-1993" data-mp="y9kmga1c0wfv">
            Shop by Category
          </h2>

          <div className="mt-7 grid grid-cols-1 gap-x-10 gap-y-8 lg:grid-cols-3" data-id="element-1994" data-mp="oc66ebjbm9o3">
            {shopCategoryGroups.map(group => <div key={group.title} data-id="element-1995" data-mp="fcsyhnauht5h">
                <h3 className="text-[14px] font-bold uppercase text-ink/60" data-id="element-1996" data-mp="fdujoklkrw0x">{group.title}</h3>
                <ul className="mt-2 border-t border-ink/20" data-id="element-1997" data-mp="2eovnfd28nfqj">
                  {group.categories.map(category => <li key={category.href} className="border-b border-ink/15" data-id="element-1998" data-mp="29rddpkgecwhh">
                      <SiteLink to={category.href} className="block min-h-[64px] py-4 transition-colors duration-150 ease-out hover:bg-water/60" data-id="element-1999" data-mp="2jista4gb4l90">
                        <span className="block text-[16.5px] font-semibold leading-6 text-pegasus-blue" data-id="element-2000" data-mp="2sk0f3n1mztm7">
                          {category.label}
                        </span>
                        <span className="mt-0.5 block text-[15.5px] leading-6 text-ink/70" data-id="element-2001" data-mp="1zwwervdho3gr">
                          {category.body}
                        </span>
                      </SiteLink>
                    </li>)}
                </ul>
              </div>)}
          </div>
        </div>
      </section>

      {/* Featured products */}
      <section aria-labelledby="featured-heading" className="w-full border-y border-ink/10 bg-white" data-id="element-2002" data-mp="1h53ow9tpuyd9">
        <div className="mx-auto max-w-container px-5 py-12 sm:px-8 lg:py-16" data-id="element-2003" data-mp="1c58wu2y7g2qm">
          <h2 id="featured-heading" className="text-h2-mobile font-semibold text-ink lg:text-h2-desktop" data-id="element-2004" data-mp="3nt3zrtetd8te">
            Featured Products
          </h2>
          <p className="mt-3 max-w-3xl text-[16px] leading-6 text-ink/75" data-id="element-2005" data-mp="3esxmo8n12aur">
            A few of the filters, care products and grilling supplies we keep on hand.
          </p>

          {visibleProducts.length > 0 ? <div className="mt-8 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4" data-id="element-2006" data-mp="bbz11u3xuee6">
              {visibleProducts.map(product => <ProductCard key={product.sku} product={product} onAddToCart={quantity => setCartCount(count => count + quantity)} data-id="element-2007" data-mp="fd12mhlbv2jb" />)}
            </div> : <p className="mt-8 text-body text-ink/75" data-id="element-2008" data-mp="2m40jlcre0kml">
              No products match that search. Try a different term, or call or text{' '}
              {business.phone} for help finding a product.
            </p>}
        </div>
      </section>

      {/* Shopping help */}
      <section aria-labelledby="help-heading" className="w-full bg-water" data-id="element-2009" data-mp="22zx3w20tu5ru">
        <div className="mx-auto max-w-container px-5 py-12 sm:px-8 lg:py-14" data-id="element-2010" data-mp="232152c7ccqof">
          <h2 id="help-heading" className="text-h2-mobile font-semibold text-ink lg:text-h2-desktop" data-id="element-2011" data-mp="1y73yrd1g4vf8">
            Shopping Help
          </h2>

          <ul className="mt-7 max-w-4xl border-t border-ink/20" data-id="element-2012" data-mp="1oaioodakku4j">
            {helpItems.map(item => <li key={item.title} className="flex flex-col gap-1 border-b border-ink/15 py-4 sm:flex-row sm:items-baseline sm:justify-between sm:gap-8" data-id="element-2013" data-mp="1jb65c2iwq56p">
                <div data-id="element-2014" data-mp="1abrgf7ie22ob">
                  <h3 className="text-[17.5px] font-semibold leading-7 text-ink" data-id="element-2015" data-mp="1acaxyn44mp1t">{item.title}</h3>
                  <p className="mt-0.5 text-[16px] leading-6 text-ink/75" data-id="element-2016" data-mp="39mslq4ep9ug1">{item.body}</p>
                </div>
                <SiteLink to={item.href} className="inline-flex min-h-[44px] shrink-0 items-center whitespace-nowrap text-[16px] font-medium text-pegasus-blue underline decoration-pegasus-blue/40 underline-offset-4 transition-colors duration-150 ease-out hover:text-pegasus-magenta hover:decoration-pegasus-magenta" data-id="element-2017" data-mp="34pjyvqilx28g">
                  {item.linkLabel}
                </SiteLink>
              </li>)}
          </ul>
        </div>
      </section>

      {/* Final navy CTA */}
      <section aria-labelledby="order-heading" className="w-full bg-ink" data-id="element-2018" data-mp="2y8qvgbxnwxp4">
        <div className="mx-auto max-w-container px-5 py-12 sm:px-8 lg:py-14" data-id="element-2019" data-mp="2y90n03ycvcwx">
          <div className="max-w-3xl" data-id="element-2020" data-mp="33727mtbfjkai">
            <p className="text-[14px] font-bold uppercase text-white/70" data-id="element-2021" data-mp="33a7ue1wl6a32">
              Local Help, Online Convenience
            </p>
            <h2 id="order-heading" className="mt-2 text-h2-mobile font-semibold text-white lg:text-h2-desktop" data-id="element-2022" data-mp="iie48lvnmn02">
              Questions Before You Order?
            </h2>
            <p className="mt-4 text-body text-white/85 sm:text-body-lg" data-id="element-2023" data-mp="l33pc823gxtl">
              Call or text us, or stop by the Brooklyn showroom with your product questions.
            </p>
          </div>

          <div className="mt-7 flex flex-wrap items-center gap-3" data-id="element-2024" data-mp="v19owj0fhrva">
            <a href={business.phoneHref} className="inline-flex min-h-[48px] items-center gap-2 whitespace-nowrap rounded-btn bg-pegasus-magenta px-6 text-[16px] font-bold text-white transition-colors duration-150 ease-out hover:bg-[#bb2f83]" data-id="element-2025" data-mp="q1eyegkkf4ar">
              <PhoneIcon className="h-4 w-4" aria-hidden="true" data-id="element-2026" data-mp="1dqq4mdkopqwa" />
              Call or Text {business.phone}
            </a>
            <SiteLink to={routes.location} className="inline-flex min-h-[48px] items-center whitespace-nowrap rounded-btn border-2 border-white/70 px-5 text-[16px] font-bold text-white transition-colors duration-150 ease-out hover:bg-white hover:text-ink" data-id="element-2027" data-mp="18r3i1diyuyat">
              Visit the Showroom
            </SiteLink>
          </div>
        </div>
      </section>
    </main>;
}